import { ButtonAlmostThere } from '@/components/sections/builder/elements/body/customer/customer_order/component/button-almost-there';
import { CustomerOrderElementDataType } from '@/components/sections/builder/elements/body/customer/customer_order/type';
import { Button } from '@/components/ui/button';
import useModal from '@/hooks/useModal';
import { cn, isEmpty } from '@/lib/utils';
import { Printer, Truck, Wallet } from 'lucide-react';

type OrderActionButtonProps = {
  status: string;
  content: CustomerOrderElementDataType;
  handlePayment: () => void;
  handleTrackOrder: () => void;
  handleInvoice: () => void;
  shippingType: 'shipping' | 'pickup_in_store';
  orderId?: string;
  setOpenModal?: (open: boolean) => void;
  modalNotify?: ReturnType<typeof useModal>;
  handleInvoiceDropship?: () => void;
  is_dropship?: boolean;
};

export default function OrderActionButton({
  status,
  content,
  handlePayment,
  handleTrackOrder,
  handleInvoice,
  shippingType,
  orderId,
  setOpenModal,
  modalNotify,
  handleInvoiceDropship,
  is_dropship,
}: OrderActionButtonProps) {
  const colorScheme = content?.colorScheme;

  // Collect buttons into an array
  const buttons: React.ReactNode[] = [];

  // Invoice button
  buttons.push(
    <StyledButton
      key='invoice'
      onClick={handleInvoice}
      testId='invoice-button-order-customer'
      colorScheme={colorScheme}
    >
      <Printer className='h-4 w-4' /> Invoice
    </StyledButton>
  );

  // Invoice Dropship button
  if (is_dropship) {
    buttons.push(
      <StyledButton
        key='invoice-dropship'
        onClick={handleInvoiceDropship ?? (() => {})}
        testId='invoice-dropship-button-order-customer'
        colorScheme={colorScheme}
      >
        <Printer className='h-4 w-4' /> Invoice (Dropshipper)
      </StyledButton>
    );
  }

  // Pay button
  if (status === 'unpaid') {
    buttons.push(
      <StyledButton
        key='pay'
        onClick={handlePayment}
        testId='pay-button-order-customer'
        colorScheme={colorScheme}
      >
        <Wallet className='h-4 w-4' /> Pay
      </StyledButton>
    );
  }

  // Track button
  if (['not_yet_shipped', 'shipping', 'shipped'].includes(status)) {
    buttons.push(
      <StyledButton
        key='track'
        onClick={handleTrackOrder}
        testId='track-button-order-customer'
        colorScheme={colorScheme}
      >
        <Truck className='h-4 w-4' /> Track Order
      </StyledButton>
    );
  }

  // Pickup in store button
  if (
    ['paid', 'ready_to_process', 'processing'].includes(status) &&
    shippingType === 'pickup_in_store' &&
    modalNotify
  ) {
    buttons.push(
      <ButtonAlmostThere
        key='pickup'
        colorScheme={colorScheme}
        orderId={orderId}
        setOpenModal={setOpenModal}
        modalNotify={modalNotify}
      />
    );
  }

  // Determine container layout based on number of buttons
  let containerClass = '';
  if (buttons.length === 1) {
    containerClass = 'flex flex-col w-full';
  } else if (buttons.length === 2) {
    containerClass = 'grid grid-cols-2 gap-2 w-full';
  } else if (buttons.length === 3) {
    containerClass = 'grid grid-cols-3 gap-2 w-full';
  } else {
    containerClass =
      'flex flex-col sm:flex-row sm:flex-wrap items-start w-full gap-2 font-normal sm:items-center';
  }

  return <div className={containerClass}>{buttons}</div>;
}

const baseButtonClass = 'w-full gap-2 px-12 py-5 sm:w-full text-sm';

const getButtonStyle = (colorScheme?: CustomerOrderElementDataType['colorScheme']) =>
  isEmpty(colorScheme)
    ? {}
    : {
        backgroundColor: colorScheme?.btnSolidBackgroud,
        color: colorScheme?.btnSolidlabel,
      };

const StyledButton = ({
  onClick,
  children,
  testId,
  colorScheme,
}: {
  onClick: () => void;
  children: React.ReactNode;
  testId?: string;
  colorScheme?: CustomerOrderElementDataType['colorScheme'];
}) => (
  <Button
    type='button'
    className={cn(baseButtonClass, isEmpty(colorScheme) && 'theme_button')}
    style={getButtonStyle(colorScheme)}
    data-testid={testId}
    onClick={onClick}
  >
    {children}
  </Button>
);
